import { FC, useContext, useEffect, useState } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box } from '@mui/material';
import {
  getNavigationActionContentPosition,
  NavigationActionVisualState,
  useNavigationActionContentWidth,
} from 'components/ui/navigationActionFeedback';
import {
  dailyNavStatusNeedsAction,
  getDailyNavReviewState,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { ReviewSaveFeedbackContext } from 'pages/Intelligence/DailyNav/hooks/useReviewSaveFeedback';
import { ReviewPriceCellProps } from './ReviewPriceCell.props';
import {
  ReviewChip,
  ReviewChipContent,
  ReviewChipSavedCheck,
  ReviewChipSavedCheckPath,
  ReviewChipSlot,
} from './ReviewPriceCell.styles';

const SAVING_LABEL = 'Saving...';
const SAVED_LABEL = 'Saved';
// Matches the slot transition, so "Saved" finishes sliding out before it unmounts.
const FEEDBACK_SLOT_EXIT_MS = 300;

const ReviewPriceCell: FC<ReviewPriceCellProps> = ({ row, onReviewClick, canReview }) => {
  const saveFeedback = useContext(ReviewSaveFeedbackContext)[row.companyId];
  const visualState = (saveFeedback ?? 'idle') as NavigationActionVisualState;

  const [isFeedbackMounted, setIsFeedbackMounted] = useState(Boolean(saveFeedback));
  const showFeedbackSlots = Boolean(saveFeedback) || isFeedbackMounted;

  useEffect(() => {
    if (saveFeedback) {
      setIsFeedbackMounted(true);
      return undefined;
    }
    const timeout = setTimeout(() => setIsFeedbackMounted(false), FEEDBACK_SLOT_EXIT_MS);
    return () => clearTimeout(timeout);
  }, [saveFeedback]);

  const isSaving = visualState === 'saving';

  // One resolver for label and color, so they cannot disagree.
  const reviewState = getDailyNavReviewState(row.record);
  const needsReview = dailyNavStatusNeedsAction(reviewState.status);

  // Reopening mid-save would show the record as it was before the save.
  const isClickable = canReview && !isSaving;

  const idleContent = (
    <>
      {/* Only the state asking for action gets the glyph. */}
      {needsReview && <WarningAmberIcon sx={{ fontSize: 11 }} />}
      {reviewState.label}
    </>
  );

  const savedContent = (
    <>
      <ReviewChipSavedCheck key={visualState === 'saved' ? 'drawn' : 'hidden'} viewBox="0 0 16 16" aria-hidden="true">
        <ReviewChipSavedCheckPath d="M3 8.25 6.5 11.5 13 5" pathLength={1} />
      </ReviewChipSavedCheck>
      {SAVED_LABEL}
    </>
  );

  const { contentWidth, idleContentRef, savedContentRef, savingContentRef } = useNavigationActionContentWidth(
    visualState,
    { errorLabel: null, idleContent: reviewState.label, savedLabel: SAVED_LABEL, savingLabel: SAVING_LABEL }
  );

  return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
      <ReviewChip
        id={`review-price-button-${row.companyId}`}
        // Review Price is for firm editors and staff; others get an inert, read-only button.
        disabled={!canReview}
        onClick={isClickable ? () => onReviewClick(row) : undefined}
        aria-busy={isSaving || undefined}
        aria-label={isSaving ? SAVING_LABEL : undefined}
        data-save-feedback={saveFeedback}
        data-testid={isSaving ? `review-price-saving-${row.companyId}` : undefined}
        chipColor={reviewState.color}
        // Read-only buttons stay flat on hover; interactive ones darken.
        chipHoverColor={isClickable ? reviewState.hoverColor : reviewState.color}
        chipTextColor={reviewState.textColor}
        isClickable={isClickable}
        isSaving={isSaving}>
        <ReviewChipContent style={contentWidth ? { width: contentWidth } : undefined}>
          <ReviewChipSlot
            ref={idleContentRef}
            aria-hidden={visualState !== 'idle' || undefined}
            position={getNavigationActionContentPosition(visualState, 'idle')}>
            {idleContent}
          </ReviewChipSlot>
          <ReviewChipSlot
            ref={savingContentRef}
            aria-hidden
            position={getNavigationActionContentPosition(visualState, 'saving')}>
            {showFeedbackSlots && SAVING_LABEL}
          </ReviewChipSlot>
          <ReviewChipSlot
            ref={savedContentRef}
            aria-hidden={visualState !== 'saved' || undefined}
            position={getNavigationActionContentPosition(visualState, 'saved')}>
            {showFeedbackSlots && savedContent}
          </ReviewChipSlot>
        </ReviewChipContent>
      </ReviewChip>
    </Box>
  );
};

export default ReviewPriceCell;
